import { z } from 'zod';

import { NO_POS } from '../ast.ts';
import { ShapeError } from '../errors.ts';
import { type AstInput, astShape } from '../normalize.ts';
import { opMeta, type RawOpCall, type RowFn } from '../op-meta.ts';
import { paramPath } from '../path.ts';
import { astArg } from '../schema/ast-codec.ts';
import { type ColumnType, structCol } from '../schema/types.ts';
import { type Cell, type Keyword, MapValue, type StructCell, type Value } from '../value.ts';

/** One `:field <expr>` pair of a `struct` call, with the AST path of its value. */
type StructFieldPair = {
  readonly field: string;
  readonly valueNode: AstInput;
  readonly valuePath: string;
};

/**
 * Decode a `(struct :field <expr> …)` call's positional params into the
 * ordered list of `:field <expr>` pairs. Each pair is a keyword at an even
 * index naming the field and the expression at the next odd index. Throws
 * `ShapeError` on an odd param count or a non-keyword field slot.
 */
const decodeStructPairs = (call: RawOpCall, path: string): readonly StructFieldPair[] => {
  const params = call.params ?? [];
  if (params.length === 0 || params.length % 2 !== 0) {
    throw new ShapeError('struct expects alternating `:field <expr>` pairs (at least one pair)', NO_POS);
  }
  return Array.from({ length: params.length / 2 }, (_, i): StructFieldPair => {
    const keyShape = astShape(params[i * 2]);
    if (keyShape.kind !== 'keyword') {
      throw new ShapeError('struct field names must be keywords (`:field`)', NO_POS);
    }
    return { field: keyShape.name, valueNode: params[i * 2 + 1] as AstInput, valuePath: paramPath(path, i * 2 + 1) };
  });
};

export const structOp = z
  .object({ name: z.literal('struct'), params: z.array(astArg()).min(2) })
  .describe(
    'Construct a struct value from named fields. (struct :field expr :field expr …) — each `:field` keyword names a struct member whose value is the following expression. Materialises one nested record cell per row.',
  )
  .register(opMeta, {
    docHidden: true,
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(struct :max 10 :min 1)', result: '{max: 10, min: 1}' },
    // Params arrive evaluated: even indices are `Keyword`s, odd indices the
    // field values. Fold them into the Value-layer struct representation, a
    // `MapValue` (the per-row cell form, a plain `StructCell`, is built on the
    // `compileExpr` path).
    apply: ({ params }: { readonly params: readonly unknown[] }): Value =>
      new MapValue(
        new Map(
          Array.from({ length: params.length / 2 }, (_, i) => [
            (params[i * 2] as Keyword).name,
            params[i * 2 + 1] as Value,
          ]),
        ),
      ),
    expr: {
      compileExpr: async (call, compile, path): Promise<RowFn> => {
        const pairs = decodeStructPairs(call, path);
        const compiled = await Promise.all(
          pairs.map(async (pair) => ({ field: pair.field, fn: await compile(pair.valueNode, pair.valuePath) })),
        );
        return (row): StructCell =>
          Object.fromEntries(compiled.map(({ field, fn }) => [field, fn(row) as Cell])) as StructCell;
      },
      inferType: (call, infer, path): ColumnType =>
        structCol(
          Object.fromEntries(
            decodeStructPairs(call, path).map(
              (pair) => [pair.field, infer(pair.valueNode, pair.valuePath) as ColumnType] as const,
            ),
          ),
        ),
    },
  });
